<script setup>
import { computed, ref, watch } from 'vue';
import { useAdminStore } from '@/stores/admin';
import userAvatar from '@/components/userAvatar.vue';
import FloatingSelect from '@/components/FloatingSelect.vue';
import InlineSpinner from '@/components/InlineSpinner.vue';

const emit = defineEmits(['select', 'update:modelValue']);
const props = defineProps({
  modelValue: { type: [Number, Object, null], default: null },
  showId: { type: Boolean, default: false },
  showAvatar: { type: Boolean, default: true },
  showAvatarInTrigger: { type: Boolean, default: true },
  showAvatarInList: { type: Boolean, default: true },
  avatarSize: { type: Number, default: 24 },
  options: { type: Array, default: null },
  optionKey: { type: String, default: 'id' },
  optionLabel: {
    type: [Function, String],
    default: (o) => {
      if (!o) return '';
      const full = [o.firstName, o.lastName].filter(Boolean).join(' ').trim();
      if (full) return full;
      if (o.name) return o.name;
      if (o.emailAddress) return o.emailAddress;
      if (o.email) return o.email;
      if (o.username) return o.username;
      return (o.id !== undefined && o.id !== null) ? `#${o.id}` : '';
    }
  },
  searchProp: { type: [Array, String], default: () => ['firstName', 'lastName', 'emailAddress'] },
  itemHeight: { type: Number, default: 44 },
  maxHeight: { type: Number, default: 340 },
  maxVisibleItems: { type: Number, default: 6 },
  matchTriggerWidth: { type: Boolean, default: true },
  placeholder: { type: String, default: 'Auswahl treffen' },
  triggerLabel: { type: String, default: '' },
  triggerIconSrc: { type: String, default: '@/assets/imgs/icons/user-solid.svg' },
  disableSort: { type: Boolean, default: false },
  allowHeadings: { type: Boolean, default: true }
});
const adminStore = useAdminStore();
const showAvatarTrigger = computed(() => props.showAvatar && props.showAvatarInTrigger);
const showAvatarList = computed(() => props.showAvatar && props.showAvatarInList);

const sorted = computed(() => {
  const base = Array.isArray(props.options) ? props.options : (adminStore.colleagues || []).filter(c => c && c.id !== -1);
  if (props.disableSort) return base.slice();
  return base.slice().sort((a, b) => {
    const aLabel = typeof props.optionLabel === 'function' ? props.optionLabel(a) : (a ? a[props.optionLabel] : '') || '';
    const bLabel = typeof props.optionLabel === 'function' ? props.optionLabel(b) : (b ? b[props.optionLabel] : '') || '';
    return String(aLabel).localeCompare(String(bLabel));
  });
});

const lastQuery = ref('');
const loading = ref(false);
let searchTimer = null;
function handleSearch(q) {
  lastQuery.value = q?.trim();
  loading.value = true;
  clearTimeout(searchTimer);
  searchTimer = setTimeout(() => { loading.value = false; }, 320);
}

const selectedOption = ref(null);
watch(() => props.modelValue, (nv) => {
  if (typeof nv === 'number') {
    selectedOption.value = sorted.value.find(c => (props.optionKey in c ? c[props.optionKey] : c.id) === nv) || null;
  } else if (nv && typeof nv === 'object') {
    selectedOption.value = nv;
  } else if (nv === null) {
    selectedOption.value = null;
  }
}, { immediate: true });

function onSelect(option) {
  if (props.allowHeadings && option && option._type === 'heading') return; // ignore heading selections
  selectedOption.value = option;
  emit('select', option);
  const outValue = option ? (typeof props.modelValue === 'number' ? (option[props.optionKey] ?? option.id) : option) : null;
  emit('update:modelValue', outValue);
}

function isSalesTeamOption(option) {
  if (!option) return false;
  const raw = option[props.optionKey] ?? option.id;
  return typeof raw === 'string' && raw.startsWith('salesTeam:');
}

function resolveOptionUserId(option) {
  if (!option) return null;
  if (isSalesTeamOption(option)) return null;
  if (option.userId !== undefined && option.userId !== null) return Number(option.userId);
  const raw = option[props.optionKey] ?? option.id;
  if (typeof raw === 'number') return raw;
  if (typeof raw === 'string') {
    const match = raw.match(/(\d+)$/);
    return match ? Number(match[1]) : null;
  }
  return null;
}

function resolveOptionSalesAgentId(option) {
  if (!option) return null;
  if (option.salesAgentId !== undefined && option.salesAgentId !== null) return Number(option.salesAgentId);
  const raw = option[props.optionKey] ?? option.id;
  if (typeof raw === 'string' && raw.startsWith('salesAgent:')) {
    const id = Number(raw.replace('salesAgent:', ''));
    return Number.isFinite(id) ? id : null;
  }
  return null;
}

function avatarBinding(option) {
  const salesAgentId = resolveOptionSalesAgentId(option);
  const userId = resolveOptionUserId(option);

  // For sales manager options, prefer salesAgentId so userAvatar can resolve status/rank reliably.
  if (salesAgentId !== null) {
    return { idUser: 0, salesAgentId };
  }

  return { idUser: userId || 0, salesAgentId: 0 };
}

function shouldRenderAvatar(option) {
  if (!option || option._type === 'heading') return false;
  if (isSalesTeamOption(option)) return false;
  const hasName = !!(option.firstName || option.lastName);
  const hasIdentifier = resolveOptionUserId(option) !== null || resolveOptionSalesAgentId(option) !== null;
  return hasName && hasIdentifier;
}
</script>

<template>
  <div class="gd-wrapper">
    <FloatingSelect size="sm" :options="sorted" :max-visible-items="maxVisibleItems" :item-height="itemHeight"
      :popover-margin="10" :match-trigger-width="props.matchTriggerWidth" :option-key="props.optionKey"
      :option-label="props.optionLabel" :search-prop="props.searchProp" aria-label="Auswahl treffen"
      :placeholder="props.placeholder" @select="onSelect" @search="handleSearch">
      <template #before-list>
        <div v-if="loading" class="fs-inline-loading" role="status" aria-live="polite">
          <InlineSpinner size="sm" label="Suche läuft..." />
        </div>
      </template>
      <template #trigger="{ open }">
        <button type="button" class="btn btn--pill btn--sm btn--outline gd-trigger" :aria-expanded="open.toString()">
          <span
            v-if="!selectedOption || !showAvatarTrigger || !shouldRenderAvatar(selectedOption)"
            class="icon-wrapper" aria-hidden="true">
            <img :src="props.triggerIconSrc" alt="" class="users-icon" />
          </span>
          <span v-if="selectedOption && showAvatarTrigger && shouldRenderAvatar(selectedOption)"
            class="trigger-avatar" :style="{ width: avatarSize + 'px', height: avatarSize + 'px' }">
            <userAvatar :idUser="avatarBinding(selectedOption).idUser"
              :salesAgentId="avatarBinding(selectedOption).salesAgentId" :size="avatarSize" :hide-text="true" :no-padding="true"
              :style="{ marginTop: '0', flexDirection: 'row' }"
              :isUserAvatar="false" :isPerformance="false" :showPoints="false" />
          </span>
          <span class="label" v-if="selectedOption">
            <span v-if="props.showId && (selectedOption.id !== undefined)" class="id">#{{ selectedOption.id }} </span>{{
              typeof props.optionLabel === 'function' ? props.optionLabel(selectedOption) :
                selectedOption[props.optionLabel] }}
          </span>
          <span class="label prefix" v-else-if="props.triggerLabel">{{ props.triggerLabel }}</span>
          <span class="label" v-else>{{ props.placeholder }}</span>
          <svg class="chevron" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
            <path fill="currentColor" d="M7 10l5 5 5-5z" />
          </svg>
        </button>
      </template>
      <template #item="{ option, highlighted }">
        <div v-if="allowHeadings && option && option._type === 'heading'" class="gd-heading"
          :class="{ 'is-highlighted': highlighted }">
          <div class="gd-heading-label">{{ option.label }}</div>
        </div>
        <div v-else class="gd-item" :class="{ 'is-highlighted': highlighted }">
          <template v-if="showAvatarList && shouldRenderAvatar(option)">
            <userAvatar :idUser="avatarBinding(option).idUser" :salesAgentId="avatarBinding(option).salesAgentId" :size="32" :hide-text="true" :no-padding="true" :style="{ marginTop: '0', flexDirection: 'row' }" :isUserAvatar="false"
              :isPerformance="false" :showPoints="false" />
          </template>
          <div class="gd-meta">
            <div class="gd-name">
              <span v-if="props.showId && (option.id !== undefined)">#{{ option.id }} </span>
              {{ typeof props.optionLabel === 'function' ? props.optionLabel(option) : option[props.optionLabel] }}
            </div>
            <div class="gd-sub" v-if="option.emailAddress">{{ option.emailAddress }}</div>
          </div>
        </div>
      </template>
      <template #empty>
        <div class="fs-empty-custom" role="status" aria-live="polite">
          <template v-if="loading">
            <InlineSpinner size="sm" label="Suche..." />
            <span>Suche...</span>
          </template>
          <template v-else-if="lastQuery">
            <span class="error-msg">Keine Treffer für "{{ lastQuery }}"</span>
          </template>
          <template v-else>
            Keine Einträge gefunden
          </template>
        </div>
      </template>
    </FloatingSelect>
    <button type="button" class="btn-clear-select external" @click="onSelect(null)" :disabled="!selectedOption"
      title="Auswahl zurücksetzen" aria-label="Auswahl löschen">
      <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="currentColor"
          d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
      </svg>
    </button>
  </div>
</template>

<style scoped>
.gd-heading {
  padding: .5rem .55rem .2rem;
  font-weight: 700;
  font-size: .58rem;
  letter-spacing: .5px;
  text-transform: uppercase;
  opacity: .55;
  border-radius: 0;
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  pointer-events: none;
  user-select: none;
}

.theme--light .gd-heading {
  opacity: .6;
}

.gd-trigger {
  display: flex;
  align-items: center;
  gap: .4rem;
  padding: .45rem .7rem;
}

.gd-trigger .chevron {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--btn-text-muted, #94a3b8);
  pointer-events: none;
}

.gd-trigger .icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 6px;
  background: var(--btn-bg-soft, rgba(255, 255, 255, 0.06));
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.05) inset;
}

.theme--light .gd-trigger .icon-wrapper {
  background: #f1f5f9;
  box-shadow: 0 0 0 1px #d9e2ec inset;
}

.gd-trigger .users-icon {
  width: 13px;
  height: 13px;
  display: block;
  opacity: .78;
  filter: brightness(1.05);
}

.theme--light .gd-trigger .users-icon {
  filter: none;
  opacity: .72;
}

.gd-trigger .label {
  font-weight: 600;
  letter-spacing: .35px;
  margin-left: .5rem;
}

.gd-trigger .label.prefix {
  opacity: .7;
  margin-right: .35rem;
}

.gd-trigger:hover .icon-wrapper {
  background: var(--btn-bg-alt, rgba(255, 255, 255, 0.12));
}

.theme--light .gd-trigger:hover .icon-wrapper {
  background: #e2e8f0;
}

.gd-trigger .trigger-avatar {
  position: relative;
  flex: 0 0 auto;
  border-radius: 50%;
  /* overflow: hidden; */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #475569, #1e293b);
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.08), 0 2px 4px -1px rgba(0, 0, 0, 0.4);
}

.theme--light .gd-trigger .trigger-avatar {
  background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
  box-shadow: 0 0 0 1px #d9e2ec, 0 1px 2px rgba(0, 0, 0, 0.08);
}

.gd-trigger .trigger-avatar .spinner.tiny {
  width: 55%;
  height: 55%;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: var(--sellwerk-color-secondary, #16a34a);
  animation: spin .6s linear infinite;
}

.theme--light .gd-trigger .trigger-avatar .spinner.tiny {
  border-color: rgba(0, 0, 0, 0.2);
  border-top-color: var(--sellwerk-color-main, #0f766e);
}

.gd-item {
  display: flex;
  align-items: center;
  gap: .55rem;
  text-align: left;
}

.gd-name {
  font-weight: 600;
  letter-spacing: .4px;
  font-size: .66rem;
}

.gd-sub {
  font-size: .55rem;
  opacity: .6;
  margin-top: .15rem;
  word-break: break-all;
}

:deep(.gd-item .sm-wrapper .txt-wrapper) {
  display: none;
}

:deep(.gd-item .sm-wrapper .rank) {
  margin: 0;
}

.fs-inline-loading {
  display: flex;
  align-items: center;
  gap: .5rem;
  font-size: .58rem;
  padding: .15rem .35rem .3rem;
  color: #c8d0d6;
}

.theme--light .fs-inline-loading {
  color: #334654;
}

.gd-wrapper {
  display: flex;
  align-items: stretch;
  gap: .4rem;
  width: 100%;
}

.gd-wrapper :deep(.floating-select) {
  flex: 1 1 auto;
  width: 100%;
}

.gd-wrapper :deep(.fs-trigger-wrapper),
.gd-wrapper :deep(.fs-trigger-wrapper > *),
.gd-wrapper .gd-trigger {
  width: 100%;
}

.gd-trigger {
  width: 100%;
  justify-content: flex-start;
}

.gd-wrapper .btn-clear-select.external {
  flex: 0 0 auto;
}

.btn-clear-select {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: .3rem;
  font-size: .55rem;
  padding: .4rem .55rem;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.05);
  color: #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
  transition: .15s;
  line-height: 1;
}

.btn-clear-select svg {
  pointer-events: none;
}

.btn-clear-select:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.35);
}

.btn-clear-select:disabled {
  opacity: .4;
  cursor: default;
}

.theme--light .btn-clear-select {
  background: #f1f5f9;
  border-color: #d5dee7;
  color: #1e293b;
}

.theme--light .btn-clear-select:hover:not(:disabled) {
  background: #e4eaf0;
  border-color: #cbd5e1;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: var(--sellwerk-color-secondary, #16a34a);
  border-radius: 50%;
  animation: spin .6s linear infinite;
}

.spinner.small {
  width: 14px;
  height: 14px;
  border-width: 2px;
}

.theme--light .spinner {
  border-color: rgba(0, 0, 0, 0.2);
  border-top-color: var(--sellwerk-color-main, #0f766e);
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.fs-empty-custom {
  display: flex;
  align-items: center;
  gap: .6rem;
  font-size: .6rem;
  padding: .55rem .4rem .65rem;
  opacity: .9;
}

.fs-empty-custom .error-msg {
  color: #f87171;
  font-weight: 600;
  letter-spacing: .3px;
}

.theme--light .fs-empty-custom .error-msg {
  color: #dc2626;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}
</style>